import * as React from "react";
import {useIntl} from "react-intl";
import {
    faAddressCard,
    faBuilding,
    faCircleInfo,
    faCreditCard,
    faEnvelope,
    faFileInvoiceDollar,
    faLanguage,
    faTag,
    faTicket,
    faUserMinus,
    faUserPen,
    faUserPlus
} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import Link from "./presentational/Link";

type Activity = {
    readonly id: string;
    readonly source: string;
    readonly type: string;
    readonly occurredAt: string;
    readonly metadata: any;
    readonly actorName: string | null;
    readonly body: string | null;
};

type Props = {
    activities: ReadonlyArray<Activity>;
};

type DayGroup = {
    label: string;
    date: Date;
    sourceBlocks: SourceBlock[];
};

type SourceBlock = {
    source: string;
    activities: Activity[];
};

const ActivityTimeline: React.FC<Props> = ({activities}) => {
    const intl = useIntl();

    // Extract name from "Name <email@example.com>" format
    const extractActorName = (actorName: string): string => {
        const match = actorName.match(/^([^<]+)/);
        return match ? match[1].trim() : actorName;
    };

    // Format message with styled ticket number pill
    const formatMessageWithTicketPill = (message: string): React.ReactNode => {
        const ticketNumberRegex = /\(#(\d+)\)/g;
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let match;

        while ((match = ticketNumberRegex.exec(message)) !== null) {
            // Add text before the match
            if (match.index > lastIndex) {
                parts.push(message.slice(lastIndex, match.index));
            }
            // Add the styled ticket number
            parts.push(
                <span
                    key={match.index}
                    className="inline-block bg-gray-100 text-gray-500 text-xs px-1 py-0.5 rounded-sm ml-1"
                >
                    #{match[1]}
                </span>
            );
            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < message.length) {
            parts.push(message.slice(lastIndex));
        }

        return parts.length > 0 ? parts : message;
    };

    const getActivityIcon = (type: string): IconDefinition => {
        if (type.includes('ticket')) return faTicket;
        if (type.includes('assigned_to_customer')) return faUserPlus;
        if (type.includes('removed_from_customer')) return faUserMinus;
        if (type.includes('attribute')) return faTag;
        if (type.includes('email')) return faEnvelope;
        if (type.includes('contact_added')) return faUserPlus;
        if (type.includes('contact_removed')) return faUserMinus;
        if (type.includes('contact_role')) return faAddressCard;
        if (type.includes('address')) return faBuilding;
        if (type.includes('tax')) return faFileInvoiceDollar;
        if (type.includes('payment_method')) return faCreditCard;
        if (type.includes('language')) return faLanguage;
        if (type.includes('renamed')) return faUserPen;
        if (type.includes('created')) return faUserPlus;
        if (type.includes('updated')) return faUserPen;
        return faCircleInfo;
    };

    const formatActivityMessage = (type: string, metadata: any): string => {
        const messageId = `activity.${type}`;
        return intl.formatMessage(
            {id: messageId, defaultMessage: type},
            metadata || {}
        );
    };

    const formatTime = (dateString: string): string => {
        const date = new Date(dateString);
        return intl.formatTime(date, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getDayLabel = (date: Date): string => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const isToday = date.toDateString() === today.toDateString();
        const isYesterday = date.toDateString() === yesterday.toDateString();
        const isCurrentYear = date.getFullYear() === today.getFullYear();

        if (isToday) {
            return intl.formatMessage({id: 'activity.today', defaultMessage: 'Today'});
        }
        if (isYesterday) {
            return intl.formatMessage({id: 'activity.yesterday', defaultMessage: 'Yesterday'});
        }
        return intl.formatDate(date, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: isCurrentYear ? undefined : 'numeric'
        });
    };

    const getSourceLabel = (source: string): string => {
        switch (source) {
            case 'CABANA': return 'Cabana';
            case 'ZAMMAD': return 'Zammad';
            case 'STRIPE': return 'Stripe';
            case 'SIPGATE': return 'Sipgate';
            default: return source;
        }
    };

    const groupActivitiesByDayAndSource = (activities: ReadonlyArray<Activity>): DayGroup[] => {
        // First, group activities by day
        const dayMap = new Map<string, { date: Date; activities: Activity[] }>();

        activities.forEach(activity => {
            const date = new Date(activity.occurredAt);
            const dayKey = date.toDateString();

            if (!dayMap.has(dayKey)) {
                dayMap.set(dayKey, {date, activities: []});
            }
            dayMap.get(dayKey)!.activities.push(activity);
        });

        const dayGroups: DayGroup[] = [];
        const sortedDays = Array.from(dayMap.entries()).sort(
            ([, a], [, b]) => b.date.getTime() - a.date.getTime()
        );

        for (const [, dayData] of sortedDays) {
            // Sort all activities of the day chronologically (newest first)
            const sortedActivities = dayData.activities.sort((a, b) =>
                new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
            );

            // Create source blocks - a new block starts when the source changes
            const sourceBlocks: SourceBlock[] = [];
            let currentBlock: SourceBlock | null = null;

            for (const activity of sortedActivities) {
                if (!currentBlock || currentBlock.source !== activity.source) {
                    // Start a new block
                    currentBlock = {
                        source: activity.source,
                        activities: []
                    };
                    sourceBlocks.push(currentBlock);
                }
                currentBlock.activities.push(activity);
            }

            dayGroups.push({
                label: getDayLabel(dayData.date),
                date: dayData.date,
                sourceBlocks
            });
        }

        return dayGroups;
    };

    const dayGroups = groupActivitiesByDayAndSource(activities);

    if (activities.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                {intl.formatMessage({id: 'activity.noActivities', defaultMessage: 'No activities yet'})}
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Timeline center line - only visible on lg screens */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />

            <div className="space-y-6">
                {dayGroups.map((dayGroup, dayIndex) => {
                    // All activities of a day are on the same side, alternates per day
                    const isLeft = dayIndex % 2 === 0;

                    return (
                        <div key={dayGroup.date.toISOString()}>
                            {/* Day separator - centered */}
                            <div className="flex justify-center mb-4 relative z-10">
                                <span className="px-3 py-1 text-xs font-medium text-amber-800 bg-amber-100 rounded-sm uppercase tracking-wide">
                                    {dayGroup.label}
                                </span>
                            </div>

                            {/* Horizontal connector line - only visible on lg screens */}
                            <div className={`hidden lg:block h-px bg-gray-200 mb-4 w-1/2 ${isLeft ? '' : 'ml-auto'}`} />

                            {/* Source blocks within a day */}
                            <div className={`space-y-4 lg:w-[calc(50%-1.5rem)] ${isLeft ? 'lg:mr-auto lg:pr-1' : 'lg:ml-auto lg:pl-1'}`}>
                                {dayGroup.sourceBlocks.map((sourceBlock, blockIndex) => (
                                    <div key={`${dayGroup.date.toISOString()}-${sourceBlock.source}-${blockIndex}`}>
                                        {/* Source header */}
                                        <div className={`mb-3 ${isLeft ? '' : 'lg:text-right'}`}>
                                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                                {getSourceLabel(sourceBlock.source)}
                                            </span>
                                        </div>

                                        {/* Activities */}
                                        <div className="space-y-3">
                                            {sourceBlock.activities.map((activity) => {
                                                const icon = getActivityIcon(activity.type);
                                                const message = formatActivityMessage(activity.type, activity.metadata);

                                                return (
                                                    <div
                                                        key={activity.id}
                                                        className={`
                                                            relative flex items-start gap-3 py-1
                                                            ${isLeft ? '' : 'lg:flex-row-reverse'}
                                                        `}
                                                    >
                                                        {/* Avatar circle with icon */}
                                                        <div className="shrink-0 w-10 h-10 rounded-full bg-red-600 flex items-center justify-center mt-0.5">
                                                            <FontAwesomeIcon icon={icon} className="w-5 h-5 text-white" />
                                                        </div>

                                                        {/* Content */}
                                                        <div className={`flex-1 min-w-0 ${isLeft ? '' : 'lg:text-right'}`}>
                                                            {activity.actorName && (
                                                                <p className={`text-xs font-semibold text-gray-500 mb-0.5 ${isLeft ? '' : 'lg:text-right'}`}>
                                                                    {extractActorName(activity.actorName)}
                                                                </p>
                                                            )}
                                                            <p className="text-sm text-gray-700 font-medium leading-5">
                                                                {formatMessageWithTicketPill(message)}
                                                            </p>
                                                            {activity.body && (
                                                                <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                                                                    {activity.body}
                                                                </p>
                                                            )}
                                                            {activity.metadata?.ticketUrl && (
                                                                <Link
                                                                    to={activity.metadata.ticketUrl}
                                                                    external
                                                                    className="text-xs mt-1 inline-block"
                                                                >
                                                                    {intl.formatMessage({id: 'activity.viewTicket', defaultMessage: 'View ticket'})} →
                                                                </Link>
                                                            )}
                                                        </div>
                                                        <div className="shrink-0 mt-0.5">
                                                            <span className="text-xs text-gray-400">
                                                                {formatTime(activity.occurredAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ActivityTimeline;
